import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  CircuitBoard, 
  CheckCircle2, 
  XCircle, 
  Eye, 
  EyeOff,
  Download,
  RotateCcw,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ImageViewer from "@/components/ImageViewer";

export default function AnalysisPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showOverlay, setShowOverlay] = useState(true);
  const [activeTab, setActiveTab] = useState("front");
  
  const { result, frontImage, backImage, ctNumber, gridSize = 2 } = location.state || {};

  useEffect(() => {
    if (!result) {
      navigate("/");
    }
  }, [result, navigate]);

  if (!result) {
    return null;
  }

  const hasCorrosion = result.has_corrosion;
  const confidence = (result.confidence * 100).toFixed(1);
  const displayCtNumber = ctNumber || result.ct_number || "N/A";

  const handleDownloadReport = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/report/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ct_number: displayCtNumber,
          has_corrosion: hasCorrosion,
          confidence: result.confidence,
          summary: result.summary,
          details: result.details,
          front_image: frontImage,
          back_image: backImage,
          front_corrosion_areas: result.front_corrosion_areas || [],
          back_corrosion_areas: result.back_corrosion_areas || [],
          timestamp: result.timestamp,
        }),
      });

      if (!response.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `corrosion-report-${displayCtNumber}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback to JSON if PDF fails
      const reportData = {
        ct_number: displayCtNumber,
        timestamp: result.timestamp,
        result: hasCorrosion ? "FAIL - CORROSION DETECTED" : "PASS - NO CORROSION",
        confidence: `${confidence}%`,
        summary: result.summary,
        details: result.details,
        front_corrosion_areas: result.front_corrosion_areas,
        back_corrosion_areas: result.back_corrosion_areas,
      };
      const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `corrosion-report-${displayCtNumber}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] relative overflow-hidden">
      {/* Grid Background */}
      <div className="absolute inset-0 grid-bg opacity-20" />
      
      {/* Content */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 py-8">
        {/* Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/")}
              variant="ghost"
              data-testid="back-button"
              className="text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-sm px-4 py-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              New Analysis
            </Button>
            
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/10 border border-cyan-500/20 rounded-sm">
                <CircuitBoard className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h1 className="font-mono font-bold text-lg tracking-tight text-zinc-50">
                  CORROSION<span className="text-cyan-400">AI</span>
                </h1>
                <p className="text-xs text-zinc-500 uppercase tracking-widest">
                  CT# {displayCtNumber}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-zinc-500">
            <Zap className="w-4 h-4 text-cyan-400" />
            <span className="text-xs font-mono uppercase tracking-wider">GPT-4o Vision</span>
          </div>
        </motion.header>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Status */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="lg:col-span-1 space-y-6"
          >
            {/* CT Number Card */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-sm p-6">
              <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-2">
                Motherboard ID
              </div>
              <div className="font-mono text-2xl font-bold text-cyan-400" data-testid="ct-number-display">
                {displayCtNumber}
              </div>
            </div>

            {/* Status Card */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-sm p-6">
              <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-4">
                Detection Status
              </div>
              
              <div 
                className={`flex items-center gap-4 mb-6 ${
                  hasCorrosion ? "text-red-500" : "text-emerald-500"
                }`}
                data-testid="status-indicator"
              >
                {hasCorrosion ? (
                  <XCircle className="w-12 h-12" />
                ) : (
                  <CheckCircle2 className="w-12 h-12" />
                )}
                <div>
                  <div 
                    className={`font-mono font-bold text-4xl uppercase tracking-tight ${
                      hasCorrosion ? "status-fail" : "status-pass"
                    }`}
                    data-testid="status-text"
                  >
                    {hasCorrosion ? "FAIL" : "PASS"}
                  </div>
                  <div className="text-zinc-400 text-sm">
                    {hasCorrosion ? "Corrosion Detected" : "No Corrosion Found"}
                  </div>
                </div>
              </div>

              {/* Confidence Score */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="font-mono text-zinc-500 uppercase tracking-widest">
                    Confidence
                  </span>
                  <span className="font-mono text-zinc-50 font-bold" data-testid="confidence-score">
                    {confidence}%
                  </span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${confidence}%` }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className={`h-full rounded-full ${
                      hasCorrosion ? "bg-red-500" : "bg-emerald-500"
                    }`}
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="border-t border-zinc-800 pt-4">
                <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-2">
                  Summary
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed" data-testid="summary-text">
                  {result.summary}
                </p>
              </div>
            </div>

            {/* Corrosion Areas Count */}
            {hasCorrosion && (
              <div className="bg-[#18181B] border border-zinc-800 rounded-sm p-6">
                <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-4">
                  Detected Areas
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 text-center">
                    <div className="font-mono text-2xl font-bold text-cyan-400" data-testid="front-areas-count">
                      {result.front_corrosion_areas?.length || 0}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">
                      Front Side
                    </div>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 text-center">
                    <div className="font-mono text-2xl font-bold text-cyan-400" data-testid="back-areas-count">
                      {result.back_corrosion_areas?.length || 0}
                    </div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">
                      Back Side
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => setShowOverlay(!showOverlay)}
                variant="outline"
                data-testid="toggle-overlay-button"
                className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 rounded-sm px-4 py-3 transition-all uppercase tracking-wide text-xs w-full justify-start"
              >
                {showOverlay ? (
                  <EyeOff className="w-4 h-4 mr-2" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                {showOverlay ? "Hide Corrosion Overlay" : "Show Corrosion Overlay"}
              </Button>
              
              <Button
                onClick={handleDownloadReport}
                variant="outline"
                data-testid="download-report-button"
                className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 rounded-sm px-4 py-3 transition-all uppercase tracking-wide text-xs w-full justify-start"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF Report
              </Button>
              
              <Button
                onClick={() => navigate("/")}
                data-testid="new-analysis-button"
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-sm px-4 py-3 transition-all uppercase tracking-wide text-xs btn-glow w-full justify-start"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                New Analysis
              </Button>
            </div>
          </motion.div>

          {/* Right Panel - Image Viewer */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="bg-[#18181B] border border-zinc-800 rounded-sm overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
                  <TabsList className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-1">
                    <TabsTrigger 
                      value="front"
                      data-testid="tab-front"
                      className="font-mono text-xs uppercase tracking-wider px-4 py-2 rounded-sm data-[state=active]:bg-cyan-500 data-[state=active]:text-black"
                    >
                      Front Side
                    </TabsTrigger>
                    <TabsTrigger 
                      value="back"
                      data-testid="tab-back"
                      className="font-mono text-xs uppercase tracking-wider px-4 py-2 rounded-sm data-[state=active]:bg-cyan-500 data-[state=active]:text-black"
                    >
                      Back Side
                    </TabsTrigger>
                  </TabsList>
                  
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <div className={`w-2 h-2 rounded-full ${showOverlay ? "bg-red-500" : "bg-zinc-600"}`} />
                    <span className="font-mono uppercase tracking-wider">
                      {showOverlay ? "Overlay Active" : "Overlay Hidden"}
                    </span>
                  </div>
                </div>

                <TabsContent value="front" className="m-0">
                  <ImageViewer
                    image={frontImage}
                    corrosionAreas={result.front_corrosion_areas || []}
                    showOverlay={showOverlay}
                    label="Front Side Analysis"
                    gridSize={gridSize}
                    data-testid="image-viewer-front"
                  />
                </TabsContent>
                
                <TabsContent value="back" className="m-0">
                  <ImageViewer
                    image={backImage}
                    corrosionAreas={result.back_corrosion_areas || []}
                    showOverlay={showOverlay}
                    label="Back Side Analysis"
                    gridSize={gridSize}
                    data-testid="image-viewer-back"
                  />
                </TabsContent>
              </Tabs>
            </div>

            {/* Detailed Analysis */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              className="mt-6 bg-[#18181B] border border-zinc-800 rounded-sm p-6"
            >
              <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-4">
                Detailed Analysis
              </div>
              <pre 
                className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap font-mono bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 max-h-[300px] overflow-y-auto"
                data-testid="detailed-analysis"
              >
                {result.details}
              </pre>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
